import { useState, useEffect } from 'react'
import { 
  getProvinces, 
  getAmphuresByProvince, 
  getDistrictsByAmphure,
  Province,
  Amphure,
  District
} from '@/lib/api/locations'

export interface LocationFormData {
  // IDs (internal use only - for API queries)
  provinceId: number | null
  amphureId: number | null
  districtId: number | null
  
  // Thai names (for display and database save)
  province: string
  amphure: string
  tambon: string
  postalCode: string
}

interface UseThaiLocationFormOptions {
  initialData?: Partial<LocationFormData>
  onError?: (error: Error) => void
}

export function useThaiLocationForm(options: UseThaiLocationFormOptions = {}) {
  const { initialData, onError } = options

  const [loading, setLoading] = useState({
    provinces: false,
    amphures: false,
    districts: false
  })

  const [formData, setFormData] = useState<LocationFormData>({
    provinceId: null,
    amphureId: null,
    districtId: null,
    province: initialData?.province || '',
    amphure: initialData?.amphure || '',
    tambon: initialData?.tambon || '',
    postalCode: initialData?.postalCode || ''
  })

  const [provinces, setProvinces] = useState<Province[]>([])
  const [amphures, setAmphures] = useState<Amphure[]>([])
  const [districts, setDistricts] = useState<District[]>([])

  // Load provinces on mount
  useEffect(() => {
    loadProvinces()
  }, [])

  async function loadProvinces() {
    setLoading(prev => ({ ...prev, provinces: true }))
    try {
      const data = await getProvinces()
      setProvinces(data)
    } catch (error) {
      console.error('Failed to load provinces:', error)
      onError?.(error as Error)
    } finally {
      setLoading(prev => ({ ...prev, provinces: false }))
    }
  }

  async function loadAmphures(provinceId: number) {
    setLoading(prev => ({ ...prev, amphures: true }))
    setAmphures([]) // Clear previous data
    try {
      const data = await getAmphuresByProvince(provinceId)
      setAmphures(data)
    } catch (error) {
      console.error('Failed to load amphures:', error)
      onError?.(error as Error)
      setAmphures([])
    } finally {
      setLoading(prev => ({ ...prev, amphures: false }))
    }
  }

  async function loadDistricts(amphureId: number) {
    setLoading(prev => ({ ...prev, districts: true }))
    setDistricts([]) // Clear previous data
    try {
      const data = await getDistrictsByAmphure(amphureId)
      setDistricts(data)
    } catch (error) {
      console.error('Failed to load districts:', error)
      onError?.(error as Error)
      setDistricts([])
    } finally {
      setLoading(prev => ({ ...prev, districts: false }))
    }
  }

  function handleProvinceChange(nameTh: string, id: number) {
    setFormData({
      provinceId: id,
      amphureId: null,
      districtId: null,
      province: nameTh,
      amphure: '',
      tambon: '',
      postalCode: ''
    })
    setAmphures([])
    setDistricts([])
    loadAmphures(id)
  }

  function handleAmphureChange(nameTh: string, id: number) {
    setFormData(prev => ({
      ...prev,
      amphureId: id,
      districtId: null,
      amphure: nameTh,
      tambon: '',
      postalCode: ''
    }))
    setDistricts([])
    loadDistricts(id)
  }

  function handleDistrictChange(nameTh: string, id: number) {
    const selected = districts.find(d => d.id === id)
    setFormData(prev => ({
      ...prev,
      districtId: id,
      tambon: nameTh,
      postalCode: selected?.zipcode || ''
    }))
  }

  function reset() {
    setFormData({
      provinceId: null,
      amphureId: null,
      districtId: null,
      province: '',
      amphure: '',
      tambon: '',
      postalCode: ''
    })
    setAmphures([])
    setDistricts([])
  }

  function setData(data: Partial<LocationFormData>) {
    setFormData(prev => ({ ...prev, ...data }))
  }

  return {
    formData,
    loading,
    provinces,
    amphures,
    districts,
    handleProvinceChange,
    handleAmphureChange,
    handleDistrictChange,
    reset,
    setData
  }
}
